<ph-card heading="Mark as paid" flat [formGroup]="markAsPaidFormGroup">
    <ph-field>
        <ph-label required>How would you like to mark this invoice as paid?</ph-label>
        <!-- Both options stay together; the chosen option's fields follow below. -->
        <ph-radio-group aria-label="How would you like to mark this invoice as paid?" [items]="recordTypeItems"
                        [value]="markAsPaidFormGroup.get('paymentRecordType')?.value"
                        (ph-change)="selectRecordType($event.detail)"></ph-radio-group>
    </ph-field>

    <ph-field *ngIf="markAsPaidFormGroup.get('paymentRecordType')?.value === PaymentRecordType.MATCH">
        <ph-label slot="label" required>Select transaction</ph-label>
        <!-- The design's "Journal entry" chip on the picked value; the closed picker shows its label only. -->
        <ph-badge *ngIf="isJournalEntrySelected" slot="label-end" variant="lighter" color="blue" size="small">Journal entry</ph-badge>
        <ph-search-select formControlName="existingTransactionToMatch" ngDefaultControl [items]="transactionItems"
                          placeholder="Select transaction"
                          search-placeholder="Search transactions"></ph-search-select>
        <ph-hint has-error
            *ngIf="markAsPaidFormGroup.get('existingTransactionToMatch').touched &&
          markAsPaidFormGroup.get('existingTransactionToMatch').hasError('required')">
            Please select a transaction
        </ph-hint>
    </ph-field>

    <!-- SD-9003: a journal-entry match asks what happens to the user's own entry. -->
    <ph-stack *ngIf="markAsPaidFormGroup.get('paymentRecordType')?.value === PaymentRecordType.MATCH && isLoadingJournalEntry">
        <ph-skeleton shape="table" rows="2" label="Loading journal entry"></ph-skeleton>
    </ph-stack>

    <ph-divider *ngIf="isJournalEntryMatch"></ph-divider>
    <ph-stack *ngIf="isJournalEntryMatch">
        <div>
            <div class="je-panel__heading">
                <i class="ph-bold ph-book-open icon-primary icon-16" aria-hidden="true"></i>
                <span>This transaction has a journal entry</span>
            </div>
            <p class="je-panel__description">
                Choose what happens to <strong>{{ journalEntryName }}</strong> when you mark this payable as paid.
            </p>
        </div>

        <ph-radio-group aria-label="What happens to the journal entry" variant="card"
                        [items]="journalEntryActionItems"
                        [value]="journalEntryAction"
                        (ph-change)="selectJournalEntryAction($event.detail)"></ph-radio-group>

        <div *ngIf="journalEntryAction === JournalEntryMatchAction.KEEP" class="je-box">
            <div class="je-box__title">{{ journalEntryName }} · will be kept</div>
            <ng-container *ngTemplateOutlet="ledgerTable; context: {rows: currentEntryRows, totals: currentEntryFooter, showHeader: true, label: 'Journal entry lines'}"></ng-container>
        </div>

        <ng-container *ngIf="journalEntryAction === JournalEntryMatchAction.REPLACE">
            <!-- Built to the design rather than ph-alert: the kit alert has no left accent bar and only a round "!" icon. -->
            <div class="je-warning" role="note">
                <i class="ph-bold ph-warning icon-warning icon-16" aria-hidden="true"></i>
                <span><strong>You're replacing an existing journal entry.</strong> {{ journalEntryName }} will be
                    overwritten with the standard entry below. This can't be undone.</span>
            </div>

            <div class="je-box je-box--removed">
                <div class="je-box__title">
                    <i class="ph-bold ph-x" aria-hidden="true"></i>
                    <span>Current entry · will be removed</span>
                </div>
                <ng-container *ngTemplateOutlet="ledgerTable; context: {rows: currentEntryRows, totals: null, showHeader: false, label: 'Current journal entry lines'}"></ng-container>
            </div>

            <div class="je-panel__arrow">
                <i class="ph-bold ph-arrow-down icon-placeholder icon-16" aria-hidden="true"></i>
            </div>

            <div class="je-box je-box--written">
                <div class="je-box__title">
                    <i class="ph-bold ph-check-circle" aria-hidden="true"></i>
                    <span>New standard entry · will be written</span>
                </div>
                <ng-container *ngTemplateOutlet="ledgerTable; context: {rows: standardEntryRows, totals: standardEntryFooter, showHeader: true, label: 'Standard payment entry lines'}"></ng-container>
            </div>

            <p class="je-panel__footnote">
                <i class="ph-bold ph-info icon-placeholder icon-12" aria-hidden="true"></i>
                {{ replaceFootnote }}
            </p>

            <!-- Core requires a bank to credit on REPLACE and a payable stores none; same picker as "Create a new
                 payment record" (product decision, Sydney). Starts on the bank the entry already credits, if any. -->
            <ph-field>
                <ph-label required>Payment bank account</ph-label>
                <app-bank-account-selector
                    [formGroup]="markAsPaidFormGroup"
                    controlName="bankAccountForTransaction"
                    [accountId]="suggestedReplaceBankAccountId ?? localPayable?.payableUnifiedBankAccountId"
                >
                </app-bank-account-selector>
                <ph-hint has-error
                    *ngIf="markAsPaidFormGroup.get('bankAccountForTransaction').touched &&
                  markAsPaidFormGroup.get('bankAccountForTransaction').hasError('required')">
                    Bank account is required
                </ph-hint>
            </ph-field>
        </ng-container>
    </ph-stack>

    <ng-template #ledgerTable let-rows="rows" let-totals="totals" let-showHeader="showHeader" let-label="label">
        <table class="je-table" [attr.aria-label]="label">
            <thead *ngIf="showHeader">
                <tr>
                    <th scope="col">Account</th>
                    <th scope="col" class="je-table__amount">Debit</th>
                    <th scope="col" class="je-table__amount">Credit</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngFor="let row of rows; trackBy: trackByLedgerRow">
                    <td>{{ row.account }}</td>
                    <td class="je-table__amount">{{ row.debit }}</td>
                    <td class="je-table__amount">{{ row.credit }}</td>
                </tr>
            </tbody>
            <tfoot *ngIf="totals">
                <tr>
                    <td>{{ totals.account }}</td>
                    <td class="je-table__amount">{{ totals.debit }}</td>
                    <td class="je-table__amount">{{ totals.credit }}</td>
                </tr>
            </tfoot>
        </table>
    </ng-template>

    <ng-container *ngIf="markAsPaidFormGroup.get('paymentRecordType')?.value === PaymentRecordType.CREATE">
        <ph-field>
            <ph-label required>Payment bank account</ph-label>
            <app-bank-account-selector
                [formGroup]="markAsPaidFormGroup"
                controlName="bankAccountForTransaction"
                [accountId]="localPayable?.payableUnifiedBankAccountId"
            >
            </app-bank-account-selector>
            <ph-hint has-error
                *ngIf="markAsPaidFormGroup.get('bankAccountForTransaction').touched &&
              markAsPaidFormGroup.get('bankAccountForTransaction').hasError('required')">
                Bank account is required
            </ph-hint>
        </ph-field>

        <ph-field>
            <ph-label required>Payment date</ph-label>
            <ph-date-input formControlName="paymentDate" ngDefaultControl></ph-date-input>
            <ph-hint has-error
                *ngIf="markAsPaidFormGroup.get('paymentDate').touched &&
                markAsPaidFormGroup.get('paymentDate').hasError('required')"
            >
                Payment date is required
            </ph-hint>
        </ph-field>

        <ph-field>
            <ph-label sub="(optional)">Memo</ph-label>
            <ph-textarea formControlName="paymentMemo" ngDefaultControl rows="2"
                         placeholder="Other invoice details"></ph-textarea>
        </ph-field>
    </ng-container>
</ph-card>

<app-confirm-dialog [open]="accrualKeepConfirmOpen"
                    heading="Keep the existing journal entry?"
                    [message]="accrualKeepConfirmMessage"
                    confirmLabel="Keep journal entry"
                    (closed)="onAccrualKeepConfirmClosed($event)"></app-confirm-dialog>
